import { z } from "zod";
import { stamps, IsoDate, Uuid } from "./common.js";
import { rateCardKind } from "./enums.js";

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

export const SupplierDto = z.object({
  id: Uuid,
  name: z.string(),
  parentGroup: z.string().nullable(),
  defaultDiscountPct: z.number().nullable(),
  notes: z.string().nullable(),
  /** Object-storage key / path of the supplier logo; null when the supplier has none (initials fallback). */
  logo: z.string().nullable(),
  isActive: z.boolean(),
  ...stamps,
});
export type Supplier = z.infer<typeof SupplierDto>;

/** Create/patch body. Optional fields accept explicit `null` to clear on PATCH. */
export const SupplierInput = z.object({
  name: z.string().min(1),
  parentGroup: z.string().nullish(),
  defaultDiscountPct: z.number().min(0).max(100).nullish(),
  notes: z.string().nullish(),
  logo: z.string().nullish(),
  /** Soft delete via PATCH `{ isActive: false }`. */
  isActive: z.boolean().default(true),
});
export type SupplierInput = z.infer<typeof SupplierInput>;

// ---------------------------------------------------------------------------
// Branches (e.g. HPS trading as Graham/Plumbase in Dover/Canterbury/Folkestone)
// ---------------------------------------------------------------------------

export const BranchDto = z.object({
  id: Uuid,
  supplierId: Uuid,
  name: z.string(),
  branchBrand: z.string().nullable(),
  town: z.string().nullable(),
  ...stamps,
});
export type Branch = z.infer<typeof BranchDto>;

/** Body for `POST/PATCH /api/suppliers/:id/branches[/:branchId]` (supplierId comes from the path). */
export const BranchInput = z.object({
  name: z.string().min(1),
  branchBrand: z.string().nullish(),
  town: z.string().nullish(),
});
export type BranchInput = z.infer<typeof BranchInput>;

// ---------------------------------------------------------------------------
// Brands & aliases
// ---------------------------------------------------------------------------

export const BrandAliasDto = z.object({
  id: Uuid,
  brandId: Uuid,
  alias: z.string(),
  ...stamps,
});
export type BrandAlias = z.infer<typeof BrandAliasDto>;

export const BrandDto = z.object({
  id: Uuid,
  name: z.string(),
  /** Included when the endpoint expands aliases (e.g. brand list/detail). */
  aliases: z.array(BrandAliasDto).optional(),
  ...stamps,
});
export type Brand = z.infer<typeof BrandDto>;

export const BrandInput = z.object({
  name: z.string().min(1),
});
export type BrandInput = z.infer<typeof BrandInput>;

/** `POST /api/brands/:id/aliases` body. */
export const BrandAliasInput = z.object({
  alias: z.string().min(1),
});
export type BrandAliasInput = z.infer<typeof BrandAliasInput>;

// ---------------------------------------------------------------------------
// Rate cards (e.g. UKPS main REIN01/414 vs developer cards REI01A-D,F /414)
// ---------------------------------------------------------------------------

export const RateCardDto = z.object({
  id: Uuid,
  supplierId: Uuid,
  termsCode: z.string(),
  name: z.string(),
  kind: rateCardKind,
  ...stamps,
});
export type RateCard = z.infer<typeof RateCardDto>;

export const RateCardInput = z.object({
  supplierId: Uuid,
  termsCode: z.string().min(1),
  name: z.string().min(1),
  kind: rateCardKind.default("main"),
});
export type RateCardInput = z.infer<typeof RateCardInput>;

// ---------------------------------------------------------------------------
// Discounts (per supplier, optionally scoped to brand/product/rate card)
// ---------------------------------------------------------------------------

export const DiscountDto = z.object({
  id: Uuid,
  supplierId: Uuid,
  brandId: Uuid.nullable(),
  productId: Uuid.nullable(),
  rateCardId: Uuid.nullable(),
  discountPct: z.number(),
  validFrom: IsoDate.nullable(),
  validTo: IsoDate.nullable(),
  ...stamps,
});
export type Discount = z.infer<typeof DiscountDto>;

export const DiscountInput = z.object({
  supplierId: Uuid,
  brandId: Uuid.nullish(),
  productId: Uuid.nullish(),
  rateCardId: Uuid.nullish(),
  discountPct: z.number().min(0).max(100),
  validFrom: IsoDate.nullish(),
  validTo: IsoDate.nullish(),
});
export type DiscountInput = z.infer<typeof DiscountInput>;
